import superagent from 'superagent';
import config from '../config';
import * as Sentry from '@sentry/react';
import { trackGatewayError } from './ErrorTracking';

import { jsonToFormData } from 'helpers/HelperFunctions';

const methods = ['get', 'post', 'put', 'patch', 'delete'];

function formatUrl(path) {
  const adjustedPath = path[0] !== '/' ? '/' + path : path;
  if (__SERVER__) {
    // Prepend host and port of the API server to the path.
    return config.apiHost + '/api' + adjustedPath;
  }
  // Prepend `/api` to relative URL, to proxy to API server.
  return '/api' + adjustedPath;
}

export default class ApiClient {
  constructor(req, res) {
    methods.forEach(
      (method) =>
        (this[method] = (path, { params, data } = {}) =>
          new Promise((resolve, reject) => {
            let newMethod = method;
            const newData = data || {};

            if (newMethod === 'patch' || newMethod === 'put') {
              newData._method = newMethod.toUpperCase();
              newMethod = 'post';
            }

            const request = superagent[newMethod](formatUrl(path));

            if (params) {
              request.query(params);
            }

            if (__SERVER__ && req.get('cookie')) {
              request.set('cookie', req.get('cookie'));
            }

            if (Object.keys(newData).length > 0) {
              // Use JSON for server-side requests and FormData for client-side
              if (__SERVER__) {
                // For server-side, use JSON
                request.set('Content-Type', 'application/json');
                request.send(newData);
              } else {
                // For client-side, use FormData as before
                const formData = new FormData();
                const formattedData = jsonToFormData(newData);

                for (const item in formattedData) {
                  if (formattedData[item] instanceof File) {
                    formData.append(
                      item,
                      formattedData[item],
                      formattedData[item].name
                    );
                  } else {
                    formData.append(item, formattedData[item]);
                  }
                }

                request.send(formData);
              }
            }

            request.end((err, { body, header, status, statusText } = {}) => {
              try {
                if (header && typeof header['set-cookie'] !== 'undefined') {
                  res.append('Set-Cookie', header['set-cookie']);
                }
              } catch (exception) {
                Sentry.captureException(exception);
                console.error(exception);
              }

              if (err) {
                // Enhance error object with additional context for better tracking
                const enhancedError = {
                  ...err,
                  ...body,
                  status: err.status || status,
                  statusText: err.statusText || statusText,
                  url: formatUrl(path),
                  method: newMethod.toUpperCase(),
                  timestamp: new Date().toISOString(),
                };

                // Track 502 and 504 errors using centralized utility
                trackGatewayError(enhancedError, {
                  source: 'api_client',
                  endpoint: path,
                  requestParams: params || {},
                  requestData:
                    Object.keys(newData).length > 0
                      ? newData
                      : 'No request data',
                  responseText: body?.message,
                });

                return reject(enhancedError);
              }

              return resolve(body);
            });
          }))
    );
  }
  /*
   * There's a V8 bug where, when using Babel, exporting classes with only
   * constructors sometimes fails. Until it's patched, this is a solution to
   * "ApiClient is not defined" from issue #14.
   * https://github.com/erikras/react-redux-universal-hot-example/issues/14
   *
   * Relevant Babel bug (but they claim it's V8): https://phabricator.babeljs.io/T2455
   *
   * Remove it at your own risk.
   */
  empty() {}
}
